import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { get, post } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { ConfirmDialog, Failure, Loading, Notice } from '../components/ui.js';
import { CommandProgress, type TrackedCommand } from '../components/CommandProgress.js';

interface SchoolDay {
  server_time: string;
  school: { id: string; name: string; code: string; timezone: string };
  date: string;
  source: 'bell_device' | 'template';
  schedule_name: string | null;
  timing: 'clock' | 'after_bell' | null;
  holiday: string | null;
  shifts: Array<{
    name: string;
    start: string;
    end: string;
    periods: Array<{ kind: 'lesson' | 'break'; label: string; start: string; end: string }>;
  }>;
  sounds: Array<{ at: string; label: string; detail: string | null }>;
  next_day: { date: string; start: string } | null;
  problems: string[];
  bell: null | {
    device_id: string;
    name: string;
    status: string;
    online: boolean;
    auto_start: boolean;
    state: null | {
      phase: string | null;
      lesson: number | null;
      timer_running: boolean;
      remaining_ms: number | null;
      audio_playing: boolean;
      emergency: boolean;
    };
    state_at: string | null;
  };
}

type BoardCommand = 'start_day' | 'skip' | 'play_anthem' | 'stop_audio' | 'reset_day';

interface BoardAction {
  type: BoardCommand;
  label: string;
  confirm: string;
  danger?: boolean;
}

const ACTIONS: BoardAction[] = [
  {
    type: 'start_day',
    label: 'بدء اليوم الدراسي',
    confirm: 'يبدأ الجرس اليوم من أوله: رسالة الصباح والنشيد ثم الحصة الأولى.',
  },
  { type: 'skip', label: 'تخطي المرحلة الحالية', confirm: 'تنتهي الحصة أو الاستراحة الجارية فورًا وتبدأ التالية.' },
  { type: 'play_anthem', label: 'تشغيل النشيد', confirm: 'يعمل النشيد الكامل على سماعات المدرسة الآن.' },
  { type: 'stop_audio', label: 'إيقاف الصوت', confirm: 'يتوقف الصوت الذي يعمل الآن. لا يوقف إنذار الطوارئ.' },
  {
    type: 'reset_day',
    label: 'إيقاف اليوم',
    confirm: 'يتوقف جدول اليوم ولا يعود للعمل تلقائيًا حتى الغد.',
    danger: true,
  },
];

const ORDINALS = ['الأولى', 'الثانية', 'الثالثة', 'الرابعة', 'الخامسة', 'السادسة', 'السابعة', 'الثامنة'];
const ordinal = (n: number | null): string => (n ? (ORDINALS[n - 1] ?? String(n)) : '');

function countdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
}

function durationText(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec % 60 !== 0) return `${sec} ث`;
  return `${sec / 60} د`;
}

function useTicker(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

function useFormatters(timeZone: string, withSeconds: boolean) {
  return useMemo(() => {
    const time = new Intl.DateTimeFormat('ar-u-nu-latn', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      ...(withSeconds ? { second: '2-digit' as const } : {}),
      hourCycle: 'h23',
    });
    const clock = new Intl.DateTimeFormat('ar-u-nu-latn', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const weekday = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone, weekday: 'long' });
    const date = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone, day: 'numeric', month: 'long', year: 'numeric' });
    let hijri: Intl.DateTimeFormat | null = null;
    try {
      hijri = new Intl.DateTimeFormat('ar-u-ca-islamic-umalqura-nu-latn', {
        timeZone,
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      hijri = null;
    }
    return {
      time: (ms: number) => time.format(ms),
      clock: (ms: number) => clock.format(ms),
      weekday: (ms: number) => weekday.format(ms),
      date: (ms: number) => date.format(ms),
      hijri: (ms: number) => (hijri ? hijri.format(ms) : ''),
    };
  }, [timeZone, withSeconds]);
}

/**
 * The school manager's board: their school's day at a glance. It replaces the
 * directorate console for school-scoped accounts, and admins can open it from
 * the rail to see what a school sees. Controls appear only with the grant.
 */
export function SchoolBoard({ preview = false }: { preview?: boolean }): JSX.Element {
  const { me, can, logout } = useAuth();
  const { schoolId: routeSchool } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);
  const [pending, setPending] = useState<BoardAction | null>(null);
  const [flash, setFlash] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  const [tracked, setTracked] = useState<TrackedCommand | null>(null);
  const clientNow = useTicker(500);

  const schools = useQuery({
    queryKey: ['schools', 'board'],
    queryFn: () => get<{ items: Array<{ id: string; name: string }> }>('/schools?page_size=200'),
  });
  const schoolId = routeSchool ?? picked ?? schools.data?.items[0]?.id ?? null;

  const day = useQuery({
    queryKey: ['school-day', schoolId],
    queryFn: () => get<SchoolDay>(`/schools/${schoolId}/today`),
    enabled: Boolean(schoolId),
    refetchInterval: 5000,
  });
  const data = day.data;

  // Count down on the server's clock, not the browser's.
  const now = clientNow + (data ? Date.parse(data.server_time) - day.dataUpdatedAt : 0);

  const shifts = useMemo(
    () =>
      (data?.shifts ?? []).map((s) => ({
        ...s,
        periods: s.periods.map((p) => ({
          ...p,
          shiftName: s.name,
          startMs: Date.parse(p.start),
          endMs: Date.parse(p.end),
        })),
      })),
    [data],
  );
  const sounds = useMemo(() => (data?.sounds ?? []).map((s) => ({ ...s, atMs: Date.parse(s.at) })), [data]);
  const periods = shifts.flatMap((s) => s.periods);
  const withSeconds = periods.some((p) => (p.endMs - p.startMs) % 60_000 !== 0);
  const fmt = useFormatters(data?.school.timezone ?? 'Asia/Baghdad', withSeconds);

  const send = useMutation({
    mutationFn: (action: BoardAction) =>
      post<{ command_id: string; expires_at: string | null }>(`/devices/${data!.bell!.device_id}/bell/commands`, {
        type: action.type,
      }),
    onSuccess: (res, action) => {
      setPending(null);
      setFlash(null);
      setTracked({
        deviceId: data!.bell!.device_id,
        commandId: res.command_id,
        deviceName: data!.bell!.name,
        action: action.label,
        expiresAt: res.expires_at,
      });
      void queryClient.invalidateQueries({ queryKey: ['school-day'] });
    },
    onError: (e: Error) => {
      setPending(null);
      setFlash({ tone: 'danger', text: e.message });
    },
  });

  const managerMode = !preview;
  const exit = managerMode ? (
    <button className="ghost" onClick={() => void logout()}>
      تسجيل الخروج
    </button>
  ) : (
    <Link to="/">العودة إلى لوحة الإدارة</Link>
  );

  if (schools.isLoading || (schoolId && day.isLoading)) {
    return (
      <div className="board">
        <Loading label="جارٍ تحميل لوحة المدرسة…" />
      </div>
    );
  }
  if (!schoolId) {
    return (
      <div className="board">
        <div className="board-header">
          <h1 className="board-school">لوحة المدرسة</h1>
          <div className="board-actions">{exit}</div>
        </div>
        <Notice tone="warn">لا توجد مدرسة ضمن صلاحيتك. تواصل مع إدارة المديرية.</Notice>
      </div>
    );
  }
  if (day.error || !data) {
    return (
      <div className="board">
        <div className="board-actions" style={{ justifyContent: 'flex-end' }}>
          {exit}
        </div>
        <Failure error={day.error} onRetry={() => void day.refetch()} />
      </div>
    );
  }

  /* ------------------------------------------------------------ now panel */
  const current = periods.find((p) => p.startMs <= now && now < p.endMs) ?? null;
  const nextSound = sounds.find((s) => s.atMs > now) ?? null;
  const first = periods[0];
  const nextDayText = data.next_day
    ? `الدوام القادم: ${fmt.weekday(Date.parse(data.next_day.start))} الساعة ${fmt.time(Date.parse(data.next_day.start))}`
    : null;

  let nowTitle: string;
  let nowRange: string | null = null;
  let nowTarget: number | null = null;
  let nowHint: string | null = null;
  let progress: number | null = null;
  if (current) {
    nowTitle = current.label;
    nowRange = `${fmt.time(current.startMs)} – ${fmt.time(current.endMs)}`;
    nowTarget = current.endMs;
    nowHint = current.kind === 'lesson' ? 'متبقٍ على نهاية الحصة' : 'متبقٍ على نهاية الاستراحة';
    progress = Math.min(100, Math.max(0, ((now - current.startMs) / (current.endMs - current.startMs)) * 100));
  } else if (data.holiday) {
    nowTitle = `عطلة: ${data.holiday}`;
    nowHint = nextDayText;
  } else if (!first) {
    nowTitle = 'لا يوجد دوام اليوم';
    nowHint = nextDayText;
  } else if (now < first.startMs) {
    nowTitle = 'قبل بدء الدوام';
    nowTarget = first.startMs;
    nowHint = `متبقٍ على بدء الدوام الساعة ${fmt.time(first.startMs)}`;
  } else {
    const upcoming = periods.find((p) => p.startMs > now);
    if (upcoming) {
      nowTitle = 'بين الدوامين';
      nowTarget = upcoming.startMs;
      nowHint = `متبقٍ على بدء «${upcoming.shiftName}» الساعة ${fmt.time(upcoming.startMs)}`;
    } else {
      nowTitle = 'انتهى دوام اليوم';
      nowHint = nextDayText;
    }
  }

  /* ----------------------------------------------------------- bell strip */
  const bell = data.bell;
  const canControl = Boolean(bell && bell.status === 'ACTIVE' && can('playback.manual'));
  let bellText = 'لا توجد حالة من الجهاز بعد';
  if (bell?.state) {
    const st = bell.state;
    const remaining =
      st.timer_running && st.remaining_ms !== null && bell.state_at
        ? Math.max(0, st.remaining_ms - (now - Date.parse(bell.state_at)))
        : null;
    const left = remaining !== null ? ` — متبقٍ ${countdown(remaining)}` : '';
    if (st.emergency) bellText = 'إنذار طوارئ يعمل';
    else if (st.phase === 'lesson') bellText = `الحصة ${ordinal(st.lesson)}${left}`;
    else if (st.phase === 'break') bellText = `استراحة بعد الحصة ${ordinal(st.lesson)}${left}`;
    else if (st.phase === 'finished') bellText = 'انتهى اليوم على الجهاز';
    else if (st.phase === 'idle') bellText = 'الجدول متوقف على الجهاز';
    else bellText = st.phase ?? '—';
    if (st.audio_playing) bellText += ' · يعمل صوت الآن';
  }

  const schoolItems = schools.data?.items ?? [];

  return (
    <div className="board">
      <header className="board-header">
        <div>
          <div className="board-kicker">لوحة المدرسة</div>
          <h1 className="board-school">{data.school.name}</h1>
          <div className="board-manager">
            {managerMode ? 'مدير المدرسة' : 'معاينة من لوحة الإدارة'}: {me?.user.display_name}
          </div>
        </div>
        <div className="board-clock">
          <div className="time num" dir="ltr">
            {fmt.clock(now)}
          </div>
          <div className="date">
            {fmt.weekday(now)} {fmt.date(now)}
          </div>
          {fmt.hijri(now) ? <div className="hijri">{fmt.hijri(now)}</div> : null}
        </div>
        <div className="board-actions">
          {schoolItems.length > 1 ? (
            <select
              aria-label="اختيار المدرسة"
              value={schoolId}
              onChange={(e) => (preview ? navigate(`/board/${e.target.value}`) : setPicked(e.target.value))}
              style={{ width: 'auto' }}
            >
              {schoolItems.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : null}
          {exit}
        </div>
      </header>

      {flash ? <Notice tone={flash.tone}>{flash.text}</Notice> : null}
      {tracked ? <CommandProgress key={tracked.commandId} command={tracked} /> : null}
      {data.problems.map((p) => (
        <Notice key={p} tone="warn">
          {p}
        </Notice>
      ))}

      <section className="board-hero">
        <div className={`board-panel ${current ? 'active' : ''}`}>
          <div className="board-kicker">الآن</div>
          <div className="board-big">{nowTitle}</div>
          {nowRange ? (
            <div className="board-sub num" dir="ltr">
              {nowRange}
            </div>
          ) : null}
          {nowTarget !== null ? (
            <div className="board-countdown num" dir="ltr">
              {countdown(nowTarget - now)}
            </div>
          ) : null}
          {nowHint ? <div className="board-sub">{nowHint}</div> : null}
          {progress !== null ? (
            <div className="board-progress" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>
          ) : null}
        </div>

        <div className={`board-panel ${nextSound ? 'active' : ''}`}>
          <div className="board-kicker">الصوت التالي</div>
          {nextSound ? (
            <>
              <div className="board-big">{nextSound.label}</div>
              <div className="board-sub">
                الساعة{' '}
                <span className="num" dir="ltr">
                  {fmt.time(nextSound.atMs)}
                </span>
                {nextSound.detail ? ` · ${nextSound.detail}` : ''}
              </div>
              <div className="board-countdown num" dir="ltr">
                {countdown(nextSound.atMs - now)}
              </div>
            </>
          ) : (
            <>
              <div className="board-big">لا أصوات أخرى اليوم</div>
              {nextDayText ? <div className="board-sub">{nextDayText}</div> : null}
            </>
          )}
        </div>
      </section>

      {bell ? (
        <section className="board-panel board-bell">
          <div className="board-status">
            <span>
              <span className={`board-dot ${bell.online ? 'on' : ''}`} aria-hidden="true" />
              جهاز الجرس «{bell.name}»: {bell.online ? 'متصل' : 'غير متصل'}
            </span>
            <span>التشغيل التلقائي: {bell.auto_start ? 'مفعّل' : 'متوقف'}</span>
            <span>على الجهاز: {bellText}</span>
          </div>
          {canControl ? (
            <div className="board-actions">
              {ACTIONS.map((a) => (
                <button
                  key={a.type}
                  className={a.danger ? 'danger' : a.type === 'start_day' ? 'primary' : ''}
                  disabled={!bell.online || send.isPending}
                  title={bell.online ? undefined : 'الجهاز غير متصل'}
                  onClick={() => setPending(a)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="board-grid">
        <section className="board-panel">
          <h2>
            جدول اليوم {data.schedule_name ? <small>{data.schedule_name}</small> : null}
          </h2>
          {shifts.length === 0 ? (
            <div className="board-sub">{data.holiday ? `عطلة: ${data.holiday}` : 'لا يوجد دوام اليوم'}</div>
          ) : (
            shifts.map((s, si) => (
              <div key={si}>
                {shifts.length > 1 ? (
                  <div className="board-shift-name">
                    {s.name} ·{' '}
                    <span className="num" dir="ltr">
                      {fmt.time(Date.parse(s.start))} – {fmt.time(Date.parse(s.end))}
                    </span>
                  </div>
                ) : null}
                <ol className="board-list">
                  {s.periods.map((p, pi) => {
                    const state = p.endMs <= now ? 'past' : p.startMs <= now ? 'current' : 'future';
                    return (
                      <li key={pi} className={`board-row ${p.kind} ${state}`}>
                        <span className="time num" dir="ltr">
                          {fmt.time(p.startMs)} – {fmt.time(p.endMs)}
                        </span>
                        <span className="label">{p.label}</span>
                        <span className="meta">
                          {state === 'current' ? (
                            <span className="num" dir="ltr">
                              {countdown(p.endMs - now)}
                            </span>
                          ) : state === 'past' ? (
                            'انتهت'
                          ) : (
                            durationText(p.endMs - p.startMs)
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))
          )}
        </section>

        <section className="board-panel">
          <h2>مواعيد الأصوات</h2>
          {sounds.length === 0 ? (
            <div className="board-sub">لا توجد أصوات مجدولة اليوم</div>
          ) : (
            <ol className="board-list">
              {sounds.map((s, i) => {
                const state = s === nextSound ? 'next' : s.atMs <= now ? 'past' : 'future';
                return (
                  <li key={i} className={`board-row sound ${state}`}>
                    <span className="time num" dir="ltr">
                      {fmt.time(s.atMs)}
                    </span>
                    <span className="label">
                      {s.label}
                      {s.detail ? <span className="detail">{s.detail}</span> : null}
                    </span>
                    <span className="meta">
                      {state === 'next' ? (
                        <span className="num" dir="ltr">
                          {countdown(s.atMs - now)}
                        </span>
                      ) : state === 'past' ? (
                        'عُزف'
                      ) : (
                        ''
                      )}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>

      {pending ? (
        <ConfirmDialog
          title={pending.label}
          danger={pending.danger}
          busy={send.isPending}
          confirmLabel="نعم، نفّذ"
          message={
            <>
              {pending.confirm}
              <br />
              يُنفَّذ على جهاز «{bell?.name}» في {data.school.name} خلال ثوانٍ.
            </>
          }
          onCancel={() => setPending(null)}
          onConfirm={() => send.mutate(pending)}
        />
      ) : null}
    </div>
  );
}
